import React, { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { PapiUser } from '@wix/da-papi-types';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import {
  AddWidgetBiEvent,
  AddWidgetClickBiEvent,
  BiData,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PlusIcon from '@wix/da-ds/pkg/Icons/Plus';
import ElementQueries from '@wix/da-shared-react/pkg/ElementQueries';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { scrollIntoViewWithHeader } from '@wix/da-shared-react/pkg/utils/scrollIntoViewWithHeader';
import { useFirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags/hooks';
import { FirstTimeFlag } from '@wix/da-shared-react/pkg/redux/firstTimeFlags';
import { URLS } from '@wix/da-url';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { ModuleType } from '../../../../types/modules';
import WidgetTypeCard from './WidgetTypeCard';
import { WithSectionContextProps } from '../../Sections/SectionContext';
import { ZoneType, InstallableModuleType } from '../../../../types/layout';
import { moduleTypeToReferralType } from './helpers';

import s from './AddWidget.scss';

export type InstallableModule = {
  moduleName: ModuleType;
  zone: ZoneType;
  position?: number;
};

export type InstallableModuleInfo = InstallableModule & InstallableModuleType;

export interface Props extends WithSectionContextProps {
  installableModules: InstallableModule[];
  installableModulesInfo: InstallableModuleInfo[];
  profileOwnerUser: PapiUser;
  hasCoreMembership?: boolean;
  hasSubmittedDeviations?: boolean;
  hasCustomBoxInstalled?: boolean;
  disabled?: boolean;
  position?: 'group-top' | 'group-bottom' | 'zone';
  addModule: (zone: ZoneType, moduleType: ModuleType, position: number) => void;
}

const AddWidget: React.FC<Props> = ({
  section,
  position,
  installableModulesInfo,
  profileOwnerUser,
  hasCoreMembership,
  hasSubmittedDeviations,
  hasCustomBoxInstalled,
  disabled,
  addModule,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [modulesFilter, setModulesFilter] = useState('');
  const logBiEvent = useBiLogger();
  const { hasFlag: hasCustomboxFTUEFlag, dropFlag: dropCustomBoxFTUEFlag } =
    useFirstTimeFlag(FirstTimeFlag.IsCustomboxNotDiscovered);

  const rootRef = useRef<HTMLDivElement>(null);
  useOnOutsideClick([rootRef], () => setIsMenuOpen(false), true);

  // T56435: /me#custom_box auto-opens whichever AddWidget
  // contains custombox, and is at the top of the zonegroup
  const moduleNames: string[] = installableModulesInfo.map(
    mod => mod.moduleName
  );
  useEffect(
    () => {
      if (
        !isMobile &&
        typeof window !== 'undefined' &&
        window.location.hash === '#custom_box' &&
        position === 'group-top' &&
        rootRef.current &&
        moduleNames.includes('papi_custombox')
      ) {
        window.location.hash = '';
        setModulesFilter('papi_custombox');
        setIsMenuOpen(true);
        scrollIntoViewWithHeader(rootRef.current, false, 0, undefined, true);
      } else {
        setModulesFilter('');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const openMenu = () => setIsMenuOpen(true);

  const closeMenu = () => {
    setIsMenuOpen(false);
    dropCustomBoxFTUEFlag();
  };

  useEffect(() => {
    if (
      hasCustomboxFTUEFlag &&
      !isMenuOpen &&
      position === 'group-top' &&
      rootRef.current &&
      moduleNames.includes('papi_custombox') &&
      hasSubmittedDeviations &&
      !hasCustomBoxInstalled
    ) {
      setModulesFilter('');
      setIsMenuOpen(true);
    }
  }, [hasCustomboxFTUEFlag, hasSubmittedDeviations]);

  return (
    <ErrorBoundary debugComponent="AddWidget" hideOnFail>
      <div
        className={classnames(
          s['root'],
          disabled && s['disabled'],
          isMenuOpen && s['menu-is-open']
        )}
        data-role="add-widget"
        ref={rootRef}
      >
        <div className={s['line']}>
          <PlainButtonOrLink
            biData={
              isMenuOpen
                ? BiData<DefaultBiEvent>({
                    evid: 2,
                    click_info: 'addWidget.close',
                  })
                : BiData<AddWidgetClickBiEvent>({
                    evid: 282,
                  })
            }
            onClick={() => {
              isMenuOpen ? closeMenu() : openMenu();
              setModulesFilter('');
            }}
            className={s['button-with-gradient-line']}
          >
            <div className={s['menu-toggle-btn']}>
              <PlusIcon className={s['plus-icon']} size={IconSize.SMALL} />
              {t(!isMenuOpen ? 'widgets.common.addWidget' : 'common.close')}
            </div>
          </PlainButtonOrLink>

          {isMenuOpen && (
            <ElementQueries
              widthBreakpoints={[920]}
              className={s['menu-container']}
            >
              <div className={s['menu']} data-role="menu">
                {installableModulesInfo
                  .filter(
                    modulesFilter
                      ? mod => mod.moduleName === modulesFilter
                      : () => true
                  )
                  .map(
                    (
                      installableModuleInfo: InstallableModuleInfo,
                      idx: number
                    ) => {
                      const {
                        zone,
                        moduleName = null,
                        position: pos = 0,
                        limit,
                        restrict,
                        installedCount = 0,
                      } = installableModuleInfo;

                      const needsCoreForThis =
                        restrict &&
                        restrict.priv === 'core' &&
                        !hasCoreMembership &&
                        installedCount >= limit;

                      return (
                        <div
                          className={s['menu-item']}
                          key={idx}
                          onClick={() => {
                            if (needsCoreForThis) {
                              const referral_info =
                                moduleTypeToReferralType(moduleName);
                              logBiEvent({
                                evid: BiEventType.BUY_CORE_BUTTON,
                                referral_info,
                              });
                              window &&
                                window.open(URLS.CORE_MEMBERSHIP, '_blank');
                            }

                            if (zone && moduleName) {
                              addModule && addModule(zone, moduleName, pos);

                              logBiEvent(
                                BiData<AddWidgetBiEvent>({
                                  evid: 283,
                                  widgetname: moduleName as string,
                                  widgetzone: zone as string,
                                })
                              );
                            }

                            setIsMenuOpen(false);
                            setModulesFilter('');
                          }}
                        >
                          <WidgetTypeCard
                            section={section}
                            zone={installableModuleInfo.zone}
                            moduleName={installableModuleInfo.moduleName}
                            profileOwnerUser={profileOwnerUser}
                            hasCoreMembership={hasCoreMembership}
                          />
                        </div>
                      );
                    }
                  )}
              </div>
            </ElementQueries>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

AddWidget.displayName = 'AddWidget';

export default AddWidget;
